import React from 'react';
import { categories } from 'choerodon-ui-font';
import noop from 'lodash/noop';
import { LabelLayout, ShowValidation } from 'choerodon-ui/pro/lib/form/enum';
import { ColumnAlign, TableQueryBarType, TableColumnResizeTriggerType } from 'choerodon-ui/pro/lib/table/enum';
import { ButtonColor, FuncType } from 'choerodon-ui/pro/lib/button/enum';
import { $l } from 'choerodon-ui/pro/lib/locale-context';
import { defaultExcludeUseColonTag } from 'choerodon-ui/pro/lib/form/utils';
import { FieldType } from 'choerodon-ui/pro/lib/data-set/enum';
import { ShowHelp } from 'choerodon-ui/pro/lib/field/enum';
import getReactNodeText from 'choerodon-ui/pro/lib/_util/getReactNodeText';
import formatReactTemplate from 'choerodon-ui/pro/lib/formatter/formatReactTemplate';
import defaultFeedback from 'choerodon-ui/pro/lib/data-set/FeedBack';
import confirm from 'choerodon-ui/pro/lib/modal/confirm';
import { Size } from '../_util/enum';
import { Action } from '../trigger/enum';
import Popover from '../popover';
const defaultTooltipTheme = target => target === 'validation' ? 'light' : 'dark';
const defaultRenderEmpty = (componentName) => {
    switch (componentName) {
        case 'Attachment':
            return $l('Attachment', 'no_attachments');
        case 'Table':
            return $l('Table', 'empty_data');
        case 'Select':
            return $l('Select', 'no_matching_results');
        case 'Output':
            return '';
        default:
    }
};
const defaultFormFieldHighlightRenderer = ({ content, hidden, ...rest }, element) => content ? (React.createElement(Popover, Object.assign({}, (hidden ? { ...rest, visible: false } : rest), { content: content }), element)) : element;
const defaultButtonProps = { color: ButtonColor.primary, funcType: FuncType.flat };
const defaultSpinProps = { size: Size.default, wrapperClassName: '' };
const defaultTableColumnAlign = (_column, field, record) => {
    if (field) {
        switch (field.get('type', record)) {
            case FieldType.number:
            case FieldType.currency:
            case FieldType.bigNumber:
                return ColumnAlign.right;
            case FieldType.boolean:
                return ColumnAlign.center;
            default:
        }
    }
};
function getComponentKey(component) {
    switch (component) {
        case 'Tabs':
            return 'tabs';
        default:
            return 'table';
    }
}
const defaultCustomizedSave = (code, customized, component) => localStorage.setItem(`${getComponentKey(component)}.customized.${code}`, JSON.stringify(customized));
const defaultCustomizedLoad = (code, component) => Promise.resolve(JSON.parse(localStorage.getItem(`${getComponentKey(component)}.customized.${code}`) || 'null'));
const defaultLovShowSelectedInView = (viewMode) => viewMode === 'drawer';
const defaults = {
    prefixCls: 'c7n',
    proPrefixCls: 'c7n-pro',
    iconfontPrefix: 'icon',
    ripple: true,
    collapseExpandIconPosition: 'left',
    collapseTrigger: 'header',
    lovTableProps: {},
    lovModalProps: {},
    lovAutoSelectSingle: false,
    lovShowSelectedInView: defaultLovShowSelectedInView,
    labelLayout: LabelLayout.horizontal,
    queryBar: TableQueryBarType.normal,
    tableBorder: true,
    tableHighLightRow: true,
    tableSelectedHighLightRow: false,
    tableRowHeight: 30,
    tableDefaultRenderer: '',
    tableColumnResizable: true,
    tableColumnHideable: true,
    performanceTableColumnHideable: true,
    tableRowDraggable: false,
    tableColumnDraggable: false,
    tableHeightChangeable: true,
    performanceTableColumnDraggable: false,
    tableColumnAlign: defaultTableColumnAlign,
    tableSpinProps: defaultSpinProps,
    tableButtonProps: defaultButtonProps,
    tableCommandProps: defaultButtonProps,
    tableAlwaysShowRowBox: false,
    tableUseMouseBatchChoose: false,
    tableEditorNextKeyEnterDown: true,
    tableAutoFocus: false,
    tableKeyboard: false,
    tableFilterSearchText: 'params',
    tableAutoHeightDiff: 80,
    tableColumnDefaultWidth: 100,
    tableColumnDefaultMinWidth: 50,
    tableColumnResizeTrigger: TableColumnResizeTriggerType.mouseDown,
    tableAggregationColumnDefaultWidth: 250,
    tableAggregationColumnDefaultMinWidth: 50,
    customizedSave: defaultCustomizedSave,
    customizedLoad: defaultCustomizedLoad,
    tableShowRemovedRow: true,
    modalSectionBorder: true,
    drawerSectionBorder: true,
    drawerTransitionName: 'slide-right',
    modalOkFirst: true,
    modalAutoCenter: false,
    modalKeyboard: true,
    modalMaskClosable: false,
    buttonColor: ButtonColor.default,
    buttonFuncType: FuncType.raised,
    renderEmpty: defaultRenderEmpty,
    icons: categories,
    dropdownMatchSelectWidth: true,
    defaultActiveFirstOption: true,
    selectReverse: true,
    selectPagingOptionContent: '···',
    selectSearchable: false,
    selectTrigger: [Action.focus, Action.click],
    useColon: false,
    excludeUseColonTagList: defaultExcludeUseColonTag,
    numberFieldNonStrictStep: false,
    showInvalidDate: true,
    highlightRenderer: defaultFormFieldHighlightRenderer,
    onPerformance: noop,
    performanceEnabled: { Table: false },
    tooltipTheme: defaultTooltipTheme,
    showValidation: ShowValidation.tooltip,
    showHelp: ShowHelp.newLine,
    numberFieldKeyboard: true,
    feedback: defaultFeedback,
    confirm: async (message) => (await confirm(message)) !== 'cancel',
    validationMessageReportFormatter: (message) => getReactNodeText(React.createElement("span", null, message)),
    validationMessageFormatter: (message, injectOptions) => message && injectOptions ? formatReactTemplate(message, injectOptions) : message,
};
export default defaults;
